import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MAP_DOT_COLORS, StatusBadge } from "./StatusBadge";
import { useLang } from "../i18n";
import geo from "./polandGeo.json";

// Real voivodeship boundaries (GUGiK-derived GeoJSON), equirectangular projection.
const { lonMin, latMax, k, cosLat } = geo.projection;
const project = (lat, lon) => [(lon - lonMin) * cosLat * k, (latMax - lat) * k];

export default function PolandMap({ gminas = [], onSelect, selectedId, testId = "plan-ogolny-tracker-map", labels = true }) {
  const navigate = useNavigate();
  const { t } = useLang();
  const [mobilePick, setMobilePick] = useState(null);
  const handle = (g) => {
    if (onSelect) return onSelect(g);
    // Mobile: first tap reveals gmina + status + date; action opens the page.
    if (typeof window !== "undefined" && window.innerWidth < 768) {
      setMobilePick(g);
      return;
    }
    navigate(`/gmina/${g.id}`);
  };
  return (
    <div>
      <svg
        data-testid={testId}
        viewBox={geo.viewBox}
        className="h-auto w-full"
        role="img"
        aria-label="Map of Poland with gmina statuses"
      >
        {geo.paths.map((p) => (
          <path
            key={p.id}
            d={p.d}
            fill="#F1F5F9"
            fillRule="evenodd"
            stroke="#94A3B8"
            strokeWidth="0.5"
            strokeLinejoin="round"
          >
            <title>{p.name}</title>
          </path>
        ))}
        {gminas.filter((g) => g.lat && g.lng).map((g) => {
          const [cx, cy] = project(g.lat, g.lng);
          const color = g.color || MAP_DOT_COLORS[g.plan_ogolny_status] || "#CBD5E1";
          return (
            <g
              key={g.id}
              data-testid={`map-gmina-${g.id}`}
              className="cursor-pointer"
              onClick={() => handle(g)}
            >
              <circle cx={cx} cy={cy} r={selectedId === g.id ? 8 : 6} fill={color} fillOpacity="0.22" />
              <circle cx={cx} cy={cy} r="3.2" fill={color} stroke="#fff" strokeWidth="0.8">
                <title>{`${g.name} — ${g.plan_ogolny_status}`}</title>
              </circle>
              <circle cx={cx} cy={cy} r="10" fill="transparent" />
              {labels && (
              <text
                x={cx + 4.5}
                y={cy + 2}
                fontSize="6"
                fontWeight="700"
                fill="#1E293B"
                stroke="#FFFFFF"
                strokeWidth="1.4"
                paintOrder="stroke"
                className="map-label"
                style={{ pointerEvents: "none" }}
              >
                {g.name}
              </text>
              )}
            </g>
          );
        })}
      </svg>
      {mobilePick && (
        <div
          data-testid="map-mobile-info"
          className="mt-3 flex items-center justify-between gap-3 rounded-lg border bg-white p-3 md:hidden"
        >
          <div>
            <p className="text-sm font-semibold text-slate-900">{mobilePick.name}</p>
            <p className="mt-0.5 text-xs text-slate-600">
              woj. {mobilePick.wojewodztwo} · {mobilePick.latest_document_date || "—"}
            </p>
            <div className="mt-1.5">
              <StatusBadge
                status={mobilePick.plan_ogolny_status}
                label={t(`status.${mobilePick.plan_ogolny_status}`)}
                testId="map-mobile-status"
              />
            </div>
          </div>
          <button
            data-testid="map-mobile-open"
            onClick={() => navigate(`/gmina/${mobilePick.id}`)}
            className="shrink-0 rounded-md bg-[#DC143C] px-3 py-2 text-xs font-bold text-white"
          >
            {t("common.view")} →
          </button>
        </div>
      )}
    </div>
  );
}
